import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { fmtCost, fmtDuration } from '../lib/format';
import { translateErrorParts } from '../lib/translate-error';
import { useAgentCalls, type AgentCallRow } from '../hooks/useAgentCalls';
import { useSplit } from './SplitView';
import { DashboardCallDetailPanel } from './DashboardCallDetailPanel';

/** Split-view detail for one agent: totals + a per-call list (time,
 *  duration, cost, status). Opened from the admin dashboard's assistant list.
 *  Clicking a call drills into its full detail (associated to our Call row by
 *  conversationId) — replacing this panel, with a Back to return here. */
export function AgentCallsPanel({
  externalAgentId,
  name,
}: {
  externalAgentId: string;
  name: string;
}) {
  const { t, i18n } = useTranslation();
  const split = useSplit();
  const { data: d, isError, error } = useAgentCalls(externalAgentId);

  function openCall(callRow: AgentCallRow) {
    const back = () =>
      split.open({
        direction: 'horizontal',
        defaultSize: 46,
        content: <AgentCallsPanel externalAgentId={externalAgentId} name={name} />,
      });
    split.open({
      direction: 'horizontal',
      defaultSize: 46,
      content: (
        <DashboardCallDetailPanel key={callRow.id} conversationId={callRow.id} fallback={callRow} onBack={back} />
      ),
    });
  }

  const dtFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }),
    [i18n.language],
  );

  const errParts = isError ? translateErrorParts(t, error) : null;

  return (
    <div className="flex h-full flex-col">
      <header className="shrink-0 border-b border-border px-4 py-3">
        <div className="text-sm font-medium text-foreground">{name}</div>
        <div className="text-xs text-muted-foreground">{t('dashboard.callsPanel.subtitle')}</div>
      </header>

      {errParts ? (
        <div className="m-4 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-xs text-destructive">
          <div className="font-medium">{errParts.title}</div>
          {errParts.description ? <div className="mt-1 opacity-90">{errParts.description}</div> : null}
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          {/* Totals */}
          <div className="grid grid-cols-3 gap-2 p-3">
            <Mini label={t('dashboard.cards.calls')} value={d ? String(d.totalCalls) : '…'} />
            <Mini label={t('dashboard.cards.time')} value={d ? fmtDuration(d.totalDurationSec) : '…'} />
            <Mini label={t('dashboard.cards.cost')} value={d ? fmtCost(d.totalCost) : '…'} />
          </div>

          {/* Per-call list */}
          {d && d.calls.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">
              {t('dashboard.callsPanel.empty')}
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-card text-left border-b border-border">
                <tr className="text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                  <th className="px-3 py-2">{t('dashboard.callsPanel.when')}</th>
                  <th className="px-3 py-2">{t('dashboard.cards.time')}</th>
                  <th className="px-3 py-2">{t('dashboard.callsPanel.status')}</th>
                  <th className="w-6" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(d?.calls ?? []).map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => openCall(c)}
                    className="cursor-pointer hover:bg-muted transition-colors"
                  >
                    <td className="px-3 py-2 whitespace-nowrap">
                      {c.startedAt ? dtFmt.format(new Date(c.startedAt)) : '—'}
                    </td>
                    <td className="px-3 py-2 tabular-nums">{fmtDuration(c.durationSec)}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {c.endedReason || c.status || '—'}
                    </td>
                    <td className="px-2 text-muted-foreground">
                      <ChevronRight className="h-4 w-4" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-card p-2 text-center">
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}
